<script setup>
import { computed, onMounted, reactive } from 'vue'
import MultistepFormActions from '@/components/Form/MultistepFormActions.vue'

const emit = defineEmits(['updateValue', 'back', 'next', 'submitStep'])

const props = defineProps({
  existingAttendees: {
    type: Array,
    default: () => []
  },
  earlyArrival: {
    type: String
  },
  data: {
    type: Object,
    required: true
  },
  event: {
    type: String,
    required: true
  },
  canGoBack: {
    type: Boolean,
    default: false
  },
  isLastStep: {
    type: Boolean,
    default: false
  },
  nextLabel: {
    type: String,
    required: true
  },
  previousLabel: {
    type: String,
    required: true
  },
  submitLabel: {
    type: String,
    required: true
  },
  stepError: {
    type: Boolean,
    default: false
  },
  stepErrorMessage: {
    type: String,
    default: ''
  }
})

const getHeadline = computed(() => {
  let text = 'Empty'
  props.data.elements.forEach((element) => {
    if (element.type === 'webform_markup') {
      text = element.markup
    }
  })
  return text
})

const state = reactive({
  activeAttendeeIndex: 0,
  attendees: [],
  isNewAttendee: true,
  formElements: {
    salutation: {
      name: 'salutation',
      type: 'select',
      label: 'Anrede',
      required: true
    },
    title: {
      name: 'title',
      type: 'text',
      label: 'Titel',
      required: false
    },
    firstName: {
      name: 'firstName',
      type: 'text',
      label: 'Vorname',
      required: true
    },
    lastName: {
      name: 'lastName',
      type: 'text',
      label: 'Nachname',
      required: true
    },
    email: {
      name: 'email',
      type: 'email',
      label: 'E-Mail',
      required: true
    },
    phone: {
      name: 'phone',
      type: 'text',
      label: 'Telefon',
      required: true
    },
    department: {
      name: 'department',
      type: 'text',
      label: 'Abteilung',
      required: true
    },
    function: {
      name: 'function',
      type: 'text',
      label: 'Position / Funktion',
      required: true
    },

  },
  salutationOptions: [
    { value: 'Herr', label: 'Herr' },
    { value: 'Frau', label: 'Frau' },
    { value: 'Divers', label: 'Divers' },
  ]
})

const createEmptyAttendee = () => ({
  event: props.event,
  salutation: '',
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  department: '',
  function: '',
  earlyArrival: false
})

onMounted(() => {
  if (props.earlyArrival === 'true') {
    state.formElements.earlyArrival = {
      name: 'earlyArrival',
      type: 'checkbox',
      label: 'Bitte buchen Sie für mich das Hotel ab dem Vorabend. Mit den zusätzlich anfallenden Kosten bin ich einverstanden',
      full: true,
      required: false
    }
  }
  state.attendees = props.existingAttendees.map((attendee) => ({ ...attendee }))
  if (state.attendees.length > 0) return
  initAttendee()
})

const initAttendee = async () => {
  state.attendees.push(createEmptyAttendee())
}

const initNewAttendee = async () => {
  state.isNewAttendee = true
  await initAttendee()
  setTimeout(() => {
    state.activeAttendeeIndex++
  }, 1000)
}

const updateValue = () => {
  emit('updateValue', 'attendees', state.attendees)
}

const handleNextStep = () => {
  updateValue()
  emit('next')
}

const handleSubmitStep = () => {
  updateValue()
  emit('submitStep')
}

const handleBackStep = () => {
  updateValue()
  emit('back')
}

const removeCurrentAttendee = () => {
  if (!state.attendees.length) {
    initAttendee()
    updateValue()
    return
  }

  if (state.attendees.length === 1) {
    state.attendees[0] = createEmptyAttendee()
    state.activeAttendeeIndex = 0
    updateValue()
    return
  }

  state.attendees.splice(state.activeAttendeeIndex, 1)
  state.activeAttendeeIndex = Math.max(0, state.activeAttendeeIndex - 1)
  updateValue()
}

</script>

<template>
  <div
    class="attendee-form"
  >
    <GridLayout
      hasGap
    >
      <LeftHeadline
        :text="getHeadline"
      />
      <div
        v-if="state.attendees.length > 0"
        class="attendee-form-form lg:col-span-8"
      >
        <div
          class="header-buttons-mobile lg:hidden flex flex-row items-center justify-start gap-4 mb-6"
        >
          <button
            v-for="(attendee, index) in state.attendees"
            class="btn text-primary bg-white text-xs h-12 w-12 rounded-full flex flex-row items-center justify-center"
            :class="{ 'btn-primary w-auto': index === state.activeAttendeeIndex }"
            @click.prevent="updateValue"
          >
              {{ `0${index + 1}` }}
          </button>
          <span
            class="text-xs text-primary"
          >
            Teilnehmer
          </span>
        </div>
        <div>
          <div
            class="attendee-edit-form grid lg:grid-cols-2 ~gap-6/8"
          >
            <div
              v-for="(field, index) in state.formElements"
              :key="index"
              class="form-input-wrapper"
            >
              <FormKit
                v-if="field.type === 'select'"
                :type="field.type"
                :label="field.label"
                :options="state.salutationOptions"
                :validation="field.required ? 'required' : null"
                messageClass="text-danger min-h-10 text-xs mt-2"
                decoratorClass="bg-primary"
                v-model="state.attendees[state.activeAttendeeIndex][field.name]"
                :placeholder="field.required ? `${field.label} *` : field.label"
                placeholderClass="text-headline"
                inputClass="w-full bg-transparent py-3 border-b border-headline"
                labelClass="visually-hidden"
              />
              <FormKit
                v-else-if="field.type === 'checkbox'"
                :type="field.type"
                :label="field.label"
                :validation="field.required ? 'required' : null"
                messageClass="text-danger min-h-10 text-xs mt-2"
                decoratorClass="bg-primary"
                v-model="state.attendees[state.activeAttendeeIndex][field.name]"
                :placeholder="field.required ? `${field.label} *` : field.label"
                placeholderClass="text-headline"
                inputClass="~h-4/5 ~w-4/5 py-3 border-b border-headline"
                labelClass="~text-xs/sm text-headline"
                wrapperClass="flex flex-row items-start gap-2"
              />
              <FormKit
                v-else
                :type="field.type"
                :label="field.label"
                :validation="field.required ? 'required' : null"
                messageClass="text-danger min-h-10 text-xs mt-2"
                decoratorClass="bg-primary"
                v-model="state.attendees[state.activeAttendeeIndex][field.name]"
                :placeholder="field.required ? `${field.label} *` : field.label"
                placeholderClass="text-headline"
                inputClass="w-full bg-transparent py-3 border-b border-headline"
                labelClass="visually-hidden"
              />
            </div>
          </div>
        </div>
        <div
          class="attendee-form-actions flex flex-col lg:flex-row flex-wrap lg:items-center justify-start gap-4 my-12 lg:mt-12"
        >
          <button
            v-for="(attendee, index) in state.attendees"
            class="btn hidden lg:flex flex-row items-center bg-primary text-white py-3 px-6 hover:px-8 transition-all duration-300 ease-in-out rounded-full ~text-xs/sm"
            @click.prevent="state.activeAttendeeIndex = index"
          >
              {{ `Teilnehmer ${index + 1}` }}
          </button>
          <button
            class="btn lg:hidden text-primary remove-attendee ~text-xs/sm flex flex-row items-center justify-start"
            @click.prevent="removeCurrentAttendee"
          >
            <span
              class="flex lg:hidden text-lg leading-none items-center justify-center w-12 h-12 mr-4 bg-white rounded-full"
            >
              x
            </span>
            Teilnehmer entfernen
          </button>
          <button
            class="btn hidden lg:flex flex-row items-center bg-white text-primary py-3 px-6 hover:px-8 transition-all duration-300 ease-in-out rounded-full ~text-xs/sm"
            @click.prevent="removeCurrentAttendee"
          >
            Teilnehmer entfernen
          </button>
          <button
            class="flex flex-row items-center lg:bg-white text-primary lg:py-3 lg:px-6 lg:hover:px-8 transition-all duration-300 ease-in-out rounded-full"
            @click.prevent="initNewAttendee()"
          >
            <span
              class="flex text-lg items-center justify-center w-12 h-12 lg:w-auto lg:h-auto mr-4 bg-white rounded-full"
            >
              +
            </span>
            Weiteren Teilnehmer hinzufügen
          </button>
        </div>
        <div class="step-headline">
          <MultistepFormActions
            :can-go-back="canGoBack"
            :is-last-step="isLastStep"
            :next-label="nextLabel"
            :previous-label="previousLabel"
            :submit-label="submitLabel"
            :step-error="stepError"
            :step-error-message="stepErrorMessage"
            @back="handleBackStep"
            @next="handleNextStep"
            @submit="handleSubmitStep"
          />
        </div>
      </div>
    </GridLayout>
  </div>
</template>

<style lang="scss" scoped>
  :deep(input), :deep(textarea) {
    &::placeholder {
      @apply text-headline opacity-60;
    }
  }
  :deep(input[type="text"]), :deep(select) {
    &:focus {
      @apply ring-black border-headline;
    }
  }

  :deep(input[type="checkbox"]:checked) {
    @apply accent-primary;
  }

  .step-headline {
    @apply flex flex-col lg:flex-row lg:justify-end mb-8;
  }
</style>
